import { ApiError } from '@thyme/libs/src/api/apiRequest'
import { HTTPStatusCode } from '@thyme/libs/src/api/types/statusCodes'
import omit from 'lodash-es/omit'
import uniqueId from 'lodash-es/uniqueId'
import { defineStore, storeToRefs } from 'pinia'

import { Subtask, SubtaskStatus } from '@/api/pathways/subtasks/types'
// nosemgrep: no-legacy-api-store
import apiStore from '@/legacy/store/modules/apiBuilder'
import { useNotificationStore } from '@/legacy/store/modules/notification'

import { usePathwaysStore } from '@/legacy/store/modules/pathways'
import { usePatientStore } from '@/legacy/store/modules/patient'
import { useTasksStore } from '@/legacy/store/modules/tasks'
import { SaveState } from '@/legacy/types/api/api'
import { DataType } from '@/legacy/types/api/apiBuilder'
import { IdMap } from '@/legacy/types/api/store'

import { EntityRole } from '@/legacy/types/entities/entities'
import {
  NotificationType,
  NotificationActions,
} from '@/legacy/types/notifications'
import { TemplateStatus } from '@/legacy/types/pathways/pathways'
import {
  BulkEditSubtaskResponse,
  GetSubtasksParams,
  MAX_NUM_SUBTASKS,
  QueueStyle,
  SubtaskOutreachResponse,
  SubtaskPayload,
  SubtaskState,
  SubtaskSuggestionParams,
  SubtaskTemplate,
  SubtaskTemplateBase,
  SubtaskTemplateDataState,
} from '@/legacy/types/pathways/subtasks'
import { Task, CreateTaskInfo } from '@/legacy/types/pathways/tasks'
import { VisibleSubtaskStatus, Stack } from '@/legacy/types/stacks'
import { GenericListResponse } from '@/libs/api/types'
import { thymeDispatch } from '@/libs/eventBus/eventBus'
import { idMapTransform, toIdMap } from '@/libs/format/objects'

// CONST
const SUBTASK_TEMPLATES_PAGE_LENGTH = 10
const STACKING_LIMIT = 5
const SUBTASK_PARTS = ['communication_maps', 'autogenerated_notes']

// Completing a subtask can fire a PARENT_TASK_CREATION autotrigger that runs as
// a background task and creates a new task in the parent pathway. The initial
// refetch in setSuccess fires before the trigger has executed, so we schedule
// a second refetch to pick up the new task.
const AUTOTRIGGER_REFETCH_DELAY_MS = 2500

const transform = (data: Subtask[]): Pick<SubtaskState, 'data'> => {
  const subtaskApi = useSubtaskApi()
  const currentSubtasksMapping = subtaskApi.data ?? {}
  const newDataMap = toIdMap(data, 'subtaskId')
  const newData = Object.assign({}, currentSubtasksMapping, newDataMap)
  return {
    ...idMapTransform({}, 'data', 'subtaskId', Object.values(newData)),
  }
}

const transformSuggestions = (data: Subtask[]): Pick<SubtaskState, 'data'> => {
  return {
    ...(data ? { data: toIdMap(data, 'subtaskId') } : { data: null }),
  }
}

const transformSubtaskStacks = (
  existingData: IdMap<Subtask[]>,
  newData: Subtask[],
  total: number,
  isPodLeadView = false
): { data: IdMap<Subtask[]>; queryMetadata: { total: number } } => {
  // Since this endpoint is a listAll(),
  // transform data to be an id map of
  // patient ID <> list of subtask stacks while
  // maintaining order for each subtask per patient
  // for every page call.
  for (const subtask of newData) {
    const memberId = subtask.memberId

    if (memberId) {
      if (!existingData[memberId]) {
        existingData[memberId] = [subtask]
      } else if (
        !existingData[memberId].some(
          (s: Subtask) => s.subtaskId === subtask.subtaskId
        ) &&
        (isPodLeadView ||
          (!isPodLeadView && existingData[memberId].length < STACKING_LIMIT))
      ) {
        existingData[memberId].push(subtask)
      }
    }
  }

  return {
    queryMetadata: { total },
    data: existingData,
  }
}

/**
 *
 * @param data
 * @param total
 * @param isPodLeadView
 */
export const transformAssignedSubtaskStacks = (
  data: Subtask[],
  total: number,
  isPodLeadView = false
) => {
  return transformSubtaskStacks({}, data, total, isPodLeadView)
}

/**
 *
 * @param data
 * @param total
 */
export const transformUnassignedSubtaskStacks = (
  data: Subtask[],
  total: number
) => {
  return transformSubtaskStacks({}, data, total)
}

/**
 *
 * @param subtaskTemplateState
 * @param data
 */
function transformSubtaskTemplateById(
  subtaskTemplateState: Partial<SubtaskTemplateDataState>,
  data: SubtaskTemplate[] | undefined
): Partial<SubtaskTemplateDataState> & Pick<SubtaskTemplateDataState, 'data'> {
  const existingData = subtaskTemplateState.data ?? {}
  const newData = data ? { data: toIdMap(data, 'variantId') } : {}
  // @ts-ignore
  // IMPROVEME(TEA-154) - This looks like `existingData` isn't getting merged in properly
  // since it's not at the `data` key
  return {
    ...omit(subtaskTemplateState, 'data'),
    // Merge data instead of overwriting it
    ...Object.assign({}, existingData, newData),
  }
}

export const transformSubtaskTemplates = (
  data: SubtaskTemplate[],
  total: number
): Partial<SubtaskTemplateDataState> &
  Pick<SubtaskTemplateDataState, 'data'> => {
  const subtaskTemplateApi = useSubtaskTemplateApi()
  const currentTemplatesMapping = subtaskTemplateApi.data ?? {}
  const newDataMap = toIdMap(data, 'variantId')
  const newData = Object.assign({}, currentTemplatesMapping, newDataMap)

  return {
    queryMetadata: { total },
    ...idMapTransform({}, 'data', 'variantId', Object.values(newData)),
  }
}

export const transformSubtaskTemplateBases = (
  data: SubtaskTemplateBase[],
  total: number
): Partial<SubtaskTemplateDataState> &
  Pick<SubtaskTemplateDataState, 'data'> => {
  const subtaskTemplateBaseApi = useSubtaskTemplateBaseApi()
  const currentTemplateBasesMapping = subtaskTemplateBaseApi.data ?? {}
  const newDataMap = toIdMap(data, 'baseTemplateId')
  const newData = Object.assign({}, currentTemplateBasesMapping, newDataMap)

  return {
    queryMetadata: { total },
    ...idMapTransform({}, 'data', 'baseTemplateId', Object.values(newData)),
  }
}

export const useSubtasksStore = defineStore('subtasks', {
  // cloneDeep due to object ref issue: https://github.com/vuejs/pinia/issues/484
  state: () => ({}),
  actions: {
    setError(
      error: string | Error,
      action: string,
      showToast: boolean,
      override_message?: string
    ) {
      if (showToast) {
        useNotificationStore().setNotification({
          message: override_message ?? `Error ${action} subtask(s)`,
          type: NotificationType.DANGER,
          error: `Error ${action} subtask: ${error}`,
        })
      }
    },

    async setSuccess(action: string, showToast: boolean) {
      const { patientId } = storeToRefs(usePatientStore())
      if (patientId.value) {
        await Promise.all([
          usePathwaysStore().getAllPathways(patientId.value),
          useTasksStore().getTasks({ filter_patient_ids: [patientId.value] }),
        ])

        if (showToast) {
          thymeDispatch('thymeline-update')
          useNotificationStore().setNotification({
            message: `Success ${action} subtask(s)`,
            type: NotificationType.SUCCESS,
          })
        }
      }
    },
    async updateSubtask(
      subtaskId: string,
      payload: Partial<SubtaskPayload>,
      headers = {},
      saveState: SaveState | undefined = undefined
    ) {
      const data = await useSubtaskApi().partialUpdate({
        body: payload,
        ids: subtaskId,
        headers,
        metaOptions: { saveState: saveState },
      })

      const error = useSubtaskApi().error
      if (error) {
        let errorMessage
        if (
          error instanceof ApiError &&
          error.status ===
            HTTPStatusCode.ERROR_412_PRECONDITION_FAILED.valueOf()
        ) {
          errorMessage = 'Failed to update subtask, subtask recently updated.'
        }

        // If 409 error this update is attempting to complete a subtask with a form
        // associated that hasn't been finalized
        else if (
          error instanceof ApiError &&
          error.status === HTTPStatusCode.ERROR_409_CONFLICT.valueOf()
        ) {
          errorMessage =
            'Failed to update subtask, form response is not finalized.'
        }
        this.setError(
          error,
          NotificationActions.UPDATE,
          !saveState,
          errorMessage
        )
        return
      }

      if (data) {
        await this.setSuccess(NotificationActions.UPDATE, !saveState)

        // If the subtask was just completed, schedule a delayed refetch to
        // pick up any tasks created by async PARENT_TASK_CREATION autotriggers.
        if (payload.status === SubtaskStatus.COMPLETED) {
          setTimeout(() => {
            void this.setSuccess(NotificationActions.UPDATE, false)
          }, AUTOTRIGGER_REFETCH_DELAY_MS)
        }
      }
      return data
    },
    async getSubtasks({
      filter_patient_ids = null,
      filter_subtask_ids = null,
      page_length = MAX_NUM_SUBTASKS,
      page_number = 1,
      filter_free_text = '',
      filter_subtask_status = null,
      filter_exclude_communication_ids = null,
      query_parts = [],
      sort_by = null,
    }: GetSubtasksParams) {
      const data = await useSubtaskApi().list({
        params: {
          ...(filter_subtask_ids ? { filter_subtask_ids } : {}),
          ...(filter_free_text ? { filter_free_text } : {}),
          ...(filter_subtask_status ? { filter_subtask_status } : {}),
          ...(page_length ? { page_length } : {}),
          ...(page_number ? { page_number } : {}),
          ...(filter_patient_ids ? { filter_patient_ids } : {}),
          ...(filter_exclude_communication_ids
            ? { filter_exclude_communication_ids }
            : {}),
          ...(sort_by ? { sort_by } : {}),
          ...(query_parts
            ? { parts: [...query_parts, ...SUBTASK_PARTS] }
            : SUBTASK_PARTS),
        },
      })

      if (useSubtaskApi().error) {
        this.setError(
          useSubtaskApi().error ?? 'unknown error',
          'fetching',
          true
        )
      } else {
        return data
      }
    },
    async getSubtaskSuggestions({
      memberId,
      communicationId,
      staffId,
      pageLength,
    }: SubtaskSuggestionParams) {
      const subtaskSuggestionsApi = useSubtaskSuggestionsApi()
      await subtaskSuggestionsApi.list({
        params: {
          member_id: memberId,
          communication_id: communicationId,
          staff_id: staffId,
          page_length: pageLength,
          page_number: 1,
        },
      })
      if (subtaskSuggestionsApi.error) {
        this.setError(subtaskSuggestionsApi.error, 'fetching', true)
      }
    },

    async createSubtasks(
      taskId: string,
      subtaskTemplateCreatePayload: Array<CreateTaskInfo>
    ) {
      const data: Subtask[] = []
      const subtaskApi = useSubtaskApi()
      useSubtaskApi().updateData(null, DataType.SINGULAR)
      for (const element of subtaskTemplateCreatePayload) {
        const res = await subtaskApi.create({
          body: {
            subtaskVariantId: element.variantId,
            dueDatetime: element.dueDatetime,
            priority: element.priority,
            flaggedBy: element.flaggedBy,
            flaggedType: element.flaggedType,
            ...(element.customSubject
              ? { customSubject: element.customSubject }
              : {}),
            taskId,
          },
        })
        if (res) {
          data.push(res)
        }
        if (subtaskApi.error) {
          this.setError(subtaskApi.error, NotificationActions.CREATE, true)
          return
        }
      }
      if (data.length) {
        await this.setSuccess(NotificationActions.CREATE, true)
      }
    },

    async getSubtaskTemplates(params: { [key: string]: any }) {
      const subtaskTemplateApi = useSubtaskTemplateApi()

      await subtaskTemplateApi.list({
        params: {
          filter_template_statuses: TemplateStatus.PUBLISHED,
          sort_by: ['title,asc'],
          ...params,
        },
      })
      if (subtaskTemplateApi.error) {
        this.setError(
          subtaskTemplateApi.error,
          'fetching subtask template',
          true
        )
      }
    },

    async getSubtaskTemplateBases({
      page_length = SUBTASK_TEMPLATES_PAGE_LENGTH,
      filter_free_text = null,
      page_number = 1,
    }: {
      page_length: number
      page_number: number
      filter_free_text: string | null
    }) {
      const subtaskTemplateBaseApi = useSubtaskTemplateBaseApi()
      await subtaskTemplateBaseApi.list({
        params: {
          ...(filter_free_text ? { filter_free_text } : {}),
          page_length,
          page_number,
        },
      })
      if (subtaskTemplateBaseApi.error) {
        this.setError(
          subtaskTemplateBaseApi.error,
          'fetching subtask template',
          true
        )
      }
    },

    async getSubtaskTemplatesById({
      filter_variant_ids,
    }: {
      filter_variant_ids: string[]
    }) {
      const subtaskTemplatesByIdApi = useSubtaskTemplatesByIdApi()
      const data = await subtaskTemplatesByIdApi.list({
        params: {
          filter_variant_ids: filter_variant_ids,
        },
      })
      if (subtaskTemplatesByIdApi.error) {
        this.setError(
          subtaskTemplatesByIdApi.error,
          `fetching subtask template ${filter_variant_ids}`,
          true
        )
      }
      return data
    },
  },
})

export const useSubtaskApi = apiStore<Subtask, IdMap<Subtask>>(
  'subtaskApi',
  '/api/subtasks',
  {
    transformData: (d: { data: Subtask[] }) => transform(d.data),
  }
)

export const useCarePlanSubtaskApi = apiStore<Subtask, IdMap<Subtask>>(
  'carePlanSubtaskApi',
  '/api/subtasks',
  {
    transformData: (d: { data: Subtask[] }) => transform(d.data),
  }
)

export const useIncompleteSubtaskApi = apiStore<Subtask, IdMap<Subtask>>(
  'incompleteSubtaskApi',
  '/api/subtasks',
  {
    transformData: (d: { data: Subtask[] }) => transform(d.data),
  }
)

export const useLastCaseConferencesSubtaskApi = apiStore<Subtask>(
  'caseConferenceSubtaskApi',
  '/api/subtasks',
  {
    transformData: (d: { data: Subtask[] }) => d,
  }
)

export const useLastGvpSubtaskApi = apiStore<Subtask>(
  'gvpSubtaskApi',
  '/api/subtasks',
  {
    transformData: (d: { data: Subtask[] }) => d,
  }
)

export const useTaskAccordionApi = apiStore<Subtask, IdMap<Subtask>>(
  'taskAccordionApi',
  '/api/subtasks',
  {
    transformData: (d: { data: Subtask[] }) => transform(d.data),
  }
)

export const useSubtaskSidebarApi = apiStore<Subtask, IdMap<Subtask>>(
  'taskAccordionApi',
  '/api/subtasks',
  {
    transformData: (d: { data: Subtask[] }) => transform(d.data),
  }
)

export const useSubtaskTemplateApi = apiStore<
  SubtaskTemplate,
  IdMap<SubtaskTemplate>
>('subtaskTemplateApi', '/api/subtask_templates', {
  transformData: (d: GenericListResponse<SubtaskTemplate>) =>
    transformSubtaskTemplates(d.data, d.queryMetadata.total),
  params: {
    parts: ['staff_completable_forms'],
  },
})

export const useSubtaskTemplatesByIdApi = apiStore<
  SubtaskTemplate,
  IdMap<SubtaskTemplate>
>('subtaskTemplatesByIdApi', '/api/subtask_templates', {
  transformData: (d: GenericListResponse<SubtaskTemplate>) =>
    transformSubtaskTemplateById({}, d.data),
  params: {
    parts: ['staff_completable_forms', 'subtask_features'],
  },
})

// Specific api for grabbing subtask suggestions
export const useSubtaskSuggestionsApi = apiStore<Subtask, IdMap<Subtask>>(
  'subtaskSuggestionsApi',
  '/api/subtasks/suggestions',
  {
    transformData: (d: { data: Subtask[] }) => transformSuggestions(d.data),
  }
)

// Specific api for grabbing tasks related to a subtask
// -- not to conflict with the taskApi which grabs tasks for a pathway
export const useSubtaskTaskApi = apiStore<Task>(
  'subtaskTaskApi',
  '/api/tasks',
  {
    transformData: (d: { data: Task[] }) => d,
  }
)

export const useSubtaskTemplateBaseApi = apiStore<
  SubtaskTemplateBase,
  IdMap<SubtaskTemplateBase>
>('subtaskTemplateBaseApi', '/api/subtask_templates/bases', {
  transformData: (d: GenericListResponse<SubtaskTemplateBase>) =>
    transformSubtaskTemplateBases(d.data, d.queryMetadata.total),
})

export const useSubtaskOutreachApi = apiStore<SubtaskOutreachResponse>(
  'subtaskOutreachApi',
  '/api/subtasks/outreach/generate',
  {}
)

// stacks ----
export const useAssignedSubtaskStacksApi = apiStore<Subtask, IdMap<Subtask[]>>(
  'assignedSubtaskStacksApi',
  '/api/subtasks',
  {
    transformData: (d: GenericListResponse<Subtask>) =>
      transformAssignedSubtaskStacks(d.data, d.queryMetadata.total),
  }
)

export const usePodLeadAssignedSubtaskStacksApi = apiStore<
  Subtask,
  IdMap<Subtask[]>
>('assignedPodLeadSubtaskStacksApi', '/api/subtasks', {
  transformData: (d: GenericListResponse<Subtask>) =>
    transformAssignedSubtaskStacks(d.data, d.queryMetadata.total, true),
})

export const useUnassignedSubtaskStacksApi = apiStore<
  Subtask,
  IdMap<Subtask[]>
>('unassignedSubtaskStacksApi', '/api/subtasks', {
  transformData: (d: GenericListResponse<Subtask>) =>
    transformUnassignedSubtaskStacks(d.data, d.queryMetadata.total),
})

// bulk edit ----
export const useBulkEditSubtasksApi = apiStore<BulkEditSubtaskResponse>(
  'bulkEditSubtasksApi',
  '/api/subtasks/bulk_edit',
  {}
)

export const useBulkEditSubtasksV2Api = apiStore<BulkEditSubtaskResponse>(
  'bulkEditSubtasksV2Api',
  '/api/subtasks/bulk/edit',
  {}
)

// Enrollment Queue
export const useUnassignedEnrollmentQueueApiWithScheduledCalls =
  apiStore<Stack>(
    'unassignedEnrollmentQueueApi',
    '/api/subtasks/enrollment_queue',
    {
      transformData: (d: { data: Stack[] }) => d,
      params: {
        filter_subtask_status: Object.values(VisibleSubtaskStatus),
        show_scheduled_calls_first: true,
        filter_queue_style: [QueueStyle.ENROLLMENT],
        filter_roles: [
          EntityRole.CLINICAL__ENROLLMENT_SPECIALIST,
          EntityRole.SPECIALIZATION__TRANSITIONS_OF_CARE_ES,
          EntityRole.SPECIALIZATION__REFERRAL_ES,
        ],
      },
    }
  )

export const useAssignedToMeEnrollmentQueueApiWithScheduledCalls =
  apiStore<Stack>(
    'assignedToMeEnrollmentQueueApi',
    '/api/subtasks/enrollment_queue',
    {
      transformData: (d: { data: Stack[] }) => d,
      params: {
        filter_subtask_status: Object.values(VisibleSubtaskStatus),
        show_scheduled_calls_first: false,
      },
    }
  )

export const useAssignedScheduledCallsSubtaskStacksApi = apiStore<
  Subtask,
  IdMap<Subtask>
>('assignedScheduledCallsSubtaskStacksApi', '/api/subtasks', {
  transformData: (d: { data: Subtask[] }) =>
    d.data ? { data: toIdMap(d.data, 'subtaskId') } : { data: null },
})

export const useUnassignedScheduledCallsSubtaskStacksApi = apiStore<
  Subtask,
  IdMap<Subtask>
>('unassignedScheduledCallsSubtaskStacksApi', '/api/subtasks', {
  transformData: (d: { data: Subtask[] }) =>
    d.data ? { data: toIdMap(d.data, 'subtaskId') } : { data: null },
})

// For care plans taskChip
// to grab subtasks for every goal task
export const createUniqueSubtaskApiStore = (uniqueStr: string) =>
  apiStore<Subtask>(`subtaskApi${uniqueId(uniqueStr)}`, '/api/subtasks', {
    transformData: (d: { data: Subtask[] }) => d,
  })
